import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SensorImport.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface ImportPreview {
  total: number;
  processed: number;
  skipped: number;
  errors: number;
  columnMapping: Record<string, string>;
  data: any[];
  skippedRows: any[];
  errorRows: any[];
  preview: any[];
}

interface ImportResult {
  created: number;
  updated: number;
  errors: any[];
  message: string;
}

const SensorImport: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoEnrich, setAutoEnrich] = useState(true);
  const [createDefinitions, setCreateDefinitions] = useState(true);
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setPreview(null);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('auto_enrich', autoEnrich.toString());
      
      const response = await axios.post(`${API_BASE_URL}/api/sensor-import/csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setPreview(response.data);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Upload');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/sensor-import/bulk`, {
        sensors: preview.data,
        create_definitions: createDefinitions
      });
      
      setResult(response.data);
      setStep('result');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Import');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API_BASE_URL}/api/sensor-import/template`, '_blank');
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setStep('upload');
  };

  return (
    <div className="sensor-import">
      <div className="import-header">
        <h1>📥 Sensor-Daten Import</h1>
        <p>Importieren Sie hunderte Sensoren in Sekunden mit CSV/Excel</p>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {step === 'upload' && (
        <div className="upload-section">
          <div className="upload-card">
            <div className="upload-icon">📄</div>
            <h2>CSV-Datei hochladen</h2>
            <p>Laden Sie Ihre Sensor-Daten als CSV-Datei hoch. Die Spalten werden automatisch erkannt.</p>
            
            <div className="upload-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={autoEnrich}
                  onChange={(e) => setAutoEnrich(e.target.checked)}
                />
                <span>🤖 KI-gestützte Auto-Vervollständigung</span>
                <small>Fehlende Felder wie Einheiten, Bereiche, Grenzwerte automatisch ergänzen</small>
              </label>
              
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={createDefinitions}
                  onChange={(e) => setCreateDefinitions(e.target.checked)}
                />
                <span>➕ Sensor-Definitionen automatisch erstellen</span>
                <small>Neue Sensor-Typen automatisch in der Datenbank anlegen</small>
              </label>
            </div>

            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                id="file-input"
              />
              <label htmlFor="file-input" className="file-input-label">
                {file ? `📄 ${file.name}` : '📁 Datei auswählen'}
              </label>
            </div>

            {file && (
              <button 
                onClick={handleUpload} 
                disabled={loading}
                className="primary-button"
              >
                {loading ? '⏳ Analysiere...' : '🔍 Vorschau anzeigen'}
              </button>
            )}

            <div className="template-section">
              <h3>Noch keine CSV-Datei?</h3>
              <p>Laden Sie unsere Vorlage herunter und füllen Sie Ihre Daten ein:</p>
              <button onClick={handleDownloadTemplate} className="secondary-button">
                ⬇️ CSV-Vorlage herunterladen
              </button>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button onClick={() => navigate('/app')} className="secondary-button">
                ← Zurück zur Hauptseite
              </button>
            </div>

            <div className="supported-columns">
              <h3>Unterstützte Spalten</h3>
              <div className="columns-grid">
                <div className="column-group">
                  <h4>Pflichtfelder</h4>
                  <ul>
                    <li>serial_number oder asset_tag</li>
                    <li>sensor_type</li>
                  </ul>
                </div>
                <div className="column-group">
                  <h4>Automatisch erkannt</h4>
                  <ul>
                    <li>manufacturer, model</li>
                    <li>location, position</li>
                    <li>unit, min_value, max_value</li>
                    <li>protocol, network_address</li>
                    <li>tag_name, opc_tag</li>
                  </ul>
                </div>
                <div className="column-group">
                  <h4>KI-ergänzt (optional)</h4>
                  <ul>
                    <li>Einheiten basierend auf Sensor-Typ</li>
                    <li>Mess-Bereiche und Grenzwerte</li>
                    <li>Kalibrierintervalle</li>
                    <li>Typische Fehlermuster</li>
                    <li>Digital Twin Referenzen</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="preview-section">
          <div className="preview-header">
            <h2>📊 Import-Vorschau</h2>
            <div className="preview-stats">
              <div className="stat-card success">
                <span className="stat-number">{preview.processed}</span>
                <span className="stat-label">Bereit zum Import</span>
              </div>
              <div className="stat-card warning">
                <span className="stat-number">{preview.skipped}</span>
                <span className="stat-label">Übersprungen</span>
              </div>
              <div className="stat-card error">
                <span className="stat-number">{preview.errors}</span>
                <span className="stat-label">Fehler</span>
              </div>
            </div>
          </div>

          <div className="column-mapping">
            <h3>🔗 Erkannte Spalten-Zuordnung</h3>
            <div className="mapping-grid">
              {Object.entries(preview.columnMapping).map(([target, source]) => (
                <div key={target} className="mapping-item">
                  <span className="source-column">{source}</span>
                  <span className="arrow">→</span>
                  <span className="target-column">{target}</span>
                </div>
              ))}
            </div>
          </div>

          {autoEnrich && (
            <div className="enrichment-info">
              <h3>🤖 KI-Anreicherung aktiv</h3>
              <p>Folgende Felder wurden automatisch ergänzt:</p>
              <ul>
                <li>✓ Einheiten basierend auf Sensor-Typ</li>
                <li>✓ Mess-Bereiche (min/max)</li>
                <li>✓ Warn- und Alarmschwellen</li>
                <li>✓ Kalibrierintervalle</li>
                <li>✓ Erwartete Lebensdauer</li>
                <li>✓ Typische Fehlermuster</li>
                <li>✓ Digital Twin Referenzen</li>
              </ul>
            </div>
          )}

          <div className="preview-data">
            <h3>📋 Datenvorschau (erste 5 Einträge)</h3>
            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Seriennummer</th>
                    <th>Asset-Tag</th>
                    <th>Typ</th>
                    <th>Einheit</th>
                    <th>Bereich</th>
                    <th>Standort</th>
                    <th>Protokoll</th>
                    <th>Tag-Name</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((item, index) => (
                    <tr key={index}>
                      <td>{item.serial_number || '-'}</td>
                      <td>{item.asset_tag || '-'}</td>
                      <td>{item.sensor_type || '-'}</td>
                      <td>{item.unit || '-'}</td>
                      <td>
                        {item.min_value && item.max_value 
                          ? `${item.min_value} - ${item.max_value}`
                          : '-'}
                      </td>
                      <td>{item.location_description || item.location || '-'}</td>
                      <td>{item.protocol || '-'}</td>
                      <td>{item.tag_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {preview.skippedRows.length > 0 && (
            <div className="skipped-rows">
              <h3>⚠️ Übersprungene Zeilen</h3>
              {preview.skippedRows.map((item, index) => (
                <div key={index} className="skipped-item">
                  <strong>Zeile {item.row}:</strong> {item.reason}
                </div>
              ))}
            </div>
          )}

          {preview.errorRows.length > 0 && (
            <div className="error-rows">
              <h3>❌ Fehlerhafte Zeilen</h3>
              {preview.errorRows.map((item, index) => (
                <div key={index} className="error-item">
                  <strong>Zeile {item.row}:</strong> {item.error}
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.1), rgba(20, 184, 166, 0.1))',
              border: '2px solid rgba(0, 180, 216, 0.4)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#00b4d8', marginBottom: '12px', textAlign: 'center' }}>
                ⏳ Importiere Sensoren...
              </h3>
              <div style={{
                width: '100%',
                height: '30px',
                background: 'rgba(148, 163, 184, 0.2)',
                borderRadius: '15px',
                overflow: 'hidden',
                marginBottom: '12px'
              }}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #00b4d8, #14b8a6)',
                  width: '100%',
                  animation: 'progress-indeterminate 2s ease-in-out infinite',
                  transformOrigin: 'left'
                }} />
              </div>
              <p style={{ textAlign: 'center', color: '#94a3b8', marginBottom: 0 }}>
                Verarbeite {preview.processed} Sensoren... Dies kann bei großen Mengen bis zu {Math.ceil((preview.processed * 0.675) / 60)} Minuten dauern.
              </p>
            </div>
          )}

          <div className="preview-actions">
            <button onClick={handleReset} className="secondary-button" disabled={loading}>
              ← Zurück
            </button>
            <button 
              onClick={handleImport} 
              disabled={loading || preview.processed === 0}
              className="primary-button"
            >
              {loading ? '⏳ Importiere...' : `✓ ${preview.processed} Sensoren importieren`}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div className="result-section">
          <div className="result-icon">
            {result.created > 0 ? '✅' : '⚠️'}
          </div>
          <h2>Import abgeschlossen</h2>
          
          <div className="result-stats">
            <div className="result-card success">
              <span className="result-number">{result.created}</span>
              <span className="result-label">Erfolgreich importiert</span>
            </div>
            {result.errors.length > 0 && (
              <div className="result-card error">
                <span className="result-number">{result.errors.length}</span>
                <span className="result-label">Fehler</span>
              </div>
            )}
          </div>

          <p className="result-message">{result.message}</p>
          
          {loading && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(234, 88, 12, 0.1))',
              border: '2px solid rgba(249, 115, 22, 0.4)',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '20px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'spin 2s linear infinite' }}>
                ⏳
              </div>
              <h3 style={{ color: '#f97316', marginBottom: '12px' }}>KI-Workflow läuft...</h3>
              <p style={{ marginBottom: '0', lineHeight: '1.6', color: '#94a3b8' }}>
                {error || 'Bitte warten Sie, während die KI Ihre Sensoren verarbeitet.'}
              </p>
            </div>
          )}
          
          {result.created > 0 && !loading && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(20, 184, 166, 0.15))',
              border: '2px solid rgba(0, 180, 216, 0.5)',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '20px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#0891b2', marginBottom: '12px', fontWeight: 600 }}>🎯 Nächster Schritt: KI-Gesamtdokumentation</h3>
              <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#1e293b', fontSize: '15px' }}>
                Sie haben <strong style={{ color: '#0891b2' }}>{result.created} Sensoren</strong> erfolgreich importiert. 
                Nutzen Sie jetzt die KI, um eine vollständige Gesamtdokumentation aller importierten Sensoren zu erstellen:
              </p>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8', color: '#334155', fontSize: '14px' }}>
                <li>📋 Automatische Zusammenfassung aller Sensor-Daten</li>
                <li>🔍 Analyse von Abhängigkeiten und Zusammenhängen</li>
                <li>📊 Übersichtliche Dokumentation im gewünschten Format</li>
                <li>⚡ In wenigen Sekunden fertig</li>
              </ul>
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="result-errors">
              <h3>Fehlerhafte Einträge</h3>
              {result.errors.slice(0, 10).map((item, index) => (
                <div key={index} className="result-error-item">
                  <strong>Sensor:</strong> {item.serial_number || item.asset_tag || item.data?.serial_number || item.data?.asset_tag || 'Unbekannt'}
                  <br />
                  <strong>Fehler:</strong> {item.error}
                  {item.details && (
                    <>
                      <br />
                      <strong>Details:</strong> {item.details}
                    </>
                  )}
                </div>
              ))}
              {result.errors.length > 10 && (
                <p className="more-errors">... und {result.errors.length - 10} weitere Fehler</p>
              )}
            </div>
          )}

          <div className="result-actions">
            <button 
              onClick={async () => {
                try {
                  setLoading(true);
                  setError('⏳ KI-Workflow wird gestartet... Bitte warten Sie.');
                  
                  // Hole alle importierten Sensor-Instanzen aus der DB
                  const sensorsResponse = await axios.get(`${API_BASE_URL}/api/sensor-instances`);
                  const allSensors = sensorsResponse.data;
                  
                  // Berechne geschätzte Dauer: ca. 675ms pro Sensor (9 Min für 800 Sensoren)
                  const estimatedMinutes = Math.ceil((allSensors.length * 0.675) / 60);
                  setError(`🤖 Verarbeite ${allSensors.length} Sensoren mit KI... Dies kann ${estimatedMinutes}-${estimatedMinutes + 1} Minuten dauern.`);
                  
                  // Bereite Daten für n8n-Workflow vor
                  const n8nWebhookUrl = process.env.REACT_APP_MCP_BASE_URL || 'https://n8n.dikitalsolutions.com/webhook';
                  
                  const workflowData = {
                    contentType: 'documentation',
                    bulkImport: true,
                    sensors: allSensors.map((sensor: any) => ({
                      sensorId: sensor.serial_number || sensor.id,
                      assetId: sensor.asset_tag || sensor.id,
                      manufacturer: sensor.manufacturer || 'Nicht angegeben',
                      model: sensor.model || 'Nicht angegeben',
                      sensorType: sensor.sensor_type,
                      location: sensor.location_description,
                      installationDate: sensor.installation_date,
                      firmware: sensor.firmware_version,
                      hardware: sensor.hardware_revision
                    })),
                    totalCount: allSensors.length,
                    outputs: ['DITA', 'AASX', 'VDI 2770']
                  };
                  
                  // Starte n8n-Workflow direkt - Timeout: 15 Minuten für große Datenmengen
                  const workflowResponse = await axios.post(`${n8nWebhookUrl}/content-factory`, workflowData, {
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    timeout: 900000
                  });
                  
                  setLoading(false);
                  setError(null);
                  
                  // Navigiere zur Hauptseite und übergebe Workflow-Ergebnis
                  navigate('/app', { 
                    state: { 
                      workflowResult: workflowResponse.data,
                      bulkImport: true,
                      sensorCount: allSensors.length
                    } 
                  });
                } catch (error) {
                  setLoading(false);
                  setError(null);
                  console.error('Fehler beim Starten des KI-Workflows:', error);
                  alert('❌ Fehler beim Starten des KI-Workflows. Bitte versuchen Sie es manuell über die Hauptseite.');
                  navigate('/app');
                }
              }}
              disabled={loading}
              className="primary-button"
              style={{ 
                background: loading 
                  ? 'linear-gradient(135deg, #666, #888)' 
                  : 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                fontSize: '1.1rem',
                padding: '16px 32px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? '⏳ Workflow läuft...' : '🤖 KI-Workflow: Gesamtdokumentation erstellen'}
            </button>
            <button onClick={() => navigate('/app')} className="secondary-button">
              ← Zurück zur Hauptseite
            </button>
            <button onClick={handleReset} className="secondary-button">
              ✓ Neuen Import starten
            </button>
            <button onClick={() => navigate('/sensor-documentation')} className="secondary-button">
              📊 Zur Sensor-Dokumentation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensorImport;
